import { getRequiredServiceOffering } from '../../../../utils/parseServiceOfferings';
import { ServiceIds } from '../../../../utils/constants/services';
import { ServicesApiClient } from '@jbhi-fi/apis/src/services/ServicesApiClient';
import { getIsServicesEnabled } from '../../../../utils/getIsServicesEnabled';
import { serviceHelper } from '../../../../config/service-helper';
import { getSession, setSession, SessionStorageKeys } from '../../../../utils/sessionStorage';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';

export const serviceTypeId = (() => {
  const isNz = window.Jbhifi.storeLocation === 'NZ';
  const isShowOnlyExtraCareEnabled = window.featureFlags?.isShowOnlyExtraCareEnabled;

  if (isNz) {
    return isShowOnlyExtraCareEnabled ? ServiceIds.ExtraCareNz : ServiceIds.AppleCareNz;
  }
  return isShowOnlyExtraCareEnabled ? ServiceIds.ExtraCare : ServiceIds.MobileExtras;
})();

export type LineItems = Array<{ Sku: number; DisplayPriceInc: number }>;

export const removePDPServiceSelection = () => {
  setSession(SessionStorageKeys.PdpServiceSelection, '{}');
};

export const setPDPServiceSelection = (type: string, sku: string, selected: boolean) => {
  const serviceSelections = JSON.parse(getSession(SessionStorageKeys.PdpServiceSelection) ?? 'null');
  setSession(
    SessionStorageKeys.PdpServiceSelection,
    JSON.stringify({
      ...serviceSelections,
      [type]: {
        ...serviceSelections[type],
        [sku]: selected,
      },
    })
  );
};

export const initPDPServiceSelections = () => {
  removePDPServiceSelection();
  removeSelectedServiceSuggestion();
};

export const getSelectedServiceSuggestion = () => {
  return JSON.parse(getSession(SessionStorageKeys.PdpSelectedServiceSuggestion) ?? 'null');
};

export const setSelectedServiceSuggestion = (suggestion: any) => {
  setSession(SessionStorageKeys.PdpSelectedServiceSuggestion, suggestion, true);
};

export const removeSelectedServiceSuggestion = () => {
  setSession(SessionStorageKeys.PdpSelectedServiceSuggestion, '{}');
};

export const isPdpServiceSelected = (type: string, sku: string) => {
  const serviceSelections = JSON.parse(getSession(SessionStorageKeys.PdpServiceSelection) ?? 'null');
  const selected = serviceSelections?.[type]?.[sku] ? true : false;
  return selected;
};

const isExtrasService = service => {
  const serviceId = service.Id || service.id;
  return serviceId === serviceTypeId;
};

export const containsExtras = services => services?.some(isExtrasService);

const getShouldRender = (services, service) => {
  if (services.length === 1) return true;
  if (containsExtras(services)) {
    return isExtrasService(service);
  }
  return true;
};

export const parseServiceSuggestions = lineItemsWithSuggestions => {
  return lineItemsWithSuggestions?.map(lineItemWithSuggestion => {
    const servicesKey = lineItemWithSuggestion.Services ? 'Services' : 'services';
    const offeringsKey = lineItemWithSuggestion.Services ? 'Offerings' : 'offerings';
    const idKey = lineItemWithSuggestion.Services ? 'Id' : 'id';
    const updatedServices = lineItemWithSuggestion[servicesKey]
      ?.filter(service => Object.values(ServiceIds).includes(service[idKey]))
      .map(service => {
        return {
          ...service,
          [offeringsKey]: getRequiredServiceOffering(
            lineItemWithSuggestion.SKU || lineItemWithSuggestion.productSku,
            service.Id || service.id,
            service[offeringsKey]
          ),
          shouldRender: getShouldRender(lineItemWithSuggestion[servicesKey], service),
        };
      });
    return { ...lineItemWithSuggestion, [servicesKey]: updatedServices };
  });
};

export const fetchServiceSuggestions = async (lineItems: LineItems) => {
  const isServicesEnabled = getIsServicesEnabled();
  const servicesApiClient = new ServicesApiClient({
    serviceSuggestionsUrl: serviceHelper.serviceSuggestionsUrl,
    countryCode: window.Jbhifi.storeLocation,
  });

  if (!isServicesEnabled) {
    return false;
  }

  const data = {
    sourceSystem: 'Shopify',
    suggestionMarkupFormat: 'Markdown',
    lineItems: lineItems.map(li => ({ productSku: li.Sku, finalProductPriceInc: li.DisplayPriceInc })),
  };
  const { data: response, error } = await servicesApiClient.getSuggestions(data);
  if (error) {
    console.error('Error while getting service suggestions: ', error);
    return false;
  }

  return response?.lineItems ? parseServiceSuggestions(response?.lineItems) : [];
};

const storefrontClient = new ShopifyStorefrontClient();

export const getProductServiceVariantId = async (
  sku: string,
  serviceId?: string,
  servicePrice?: number
): Promise<number> => {
  const isCheckout2Enabled = window.Jbhifi.isCheckoutExtensibilityEnabled;

  const handle = isCheckout2Enabled ? `service-${sku}` : `${serviceId}-${sku}-${Math.trunc(servicePrice ?? 0)}`;

  const product = await storefrontClient.getProductByHandle(handle || '');

  const variant = isCheckout2Enabled
    ? product?.variants?.nodes?.[0]
    : product?.variants.nodes.find(variant => parseFloat(variant.price.amount) === servicePrice);

  if (!variant) throw 'unable to find shopify variant for service';

  return Number(variant?.id.split('/').pop());
};

export const IsAttachedService = (item: JbCartItem) => {
  return item.properties?._isAttachedService?.toString() === 'true';
};
